import { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, MagnifyingGlass, UserCircle, Eye, CaretLeft, CaretRight, ClipboardText } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { confirmDialog } from '@/components/ConfirmDialog';
import { NIVO_OBRAZOVANJA } from '../constants/hrConstants';

const fmtDate = (d) => {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
    const [y, m, dd] = d.substring(0, 10).split('-');
    return `${dd}.${m}.${y}`;
  }
  try {
    const dt = new Date(d);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${dt.getFullYear()}`;
  } catch { return d; }
};

const PAGE_SIZE = 50;

export default function FizickaLicaPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDetail, setShowDetail] = useState(null);

  const emptyForm = {
    ime: '', prezime: '', jmb: '', djevojacko_prezime: '', ime_roditelja: '',
    datum_rodjenja: '', mjesto_rodjenja: '', opstina_rodjenja: '', drzava_rodjenja: '',
    pol: '', drzavljanstvo: '', vrsta_dokumenta: '', broj_dokumenta: '', dokument_izdat: '',
    stepen_strucnosti: '', zanimanje: '', osnov_prestanka: '',
    drzava: '', opstina: '', mjesto: '', adresa: '', telefon: '', email: '', client_id: '', napomena: '', aktivan: true,
    vrsta_lica: 'domace',
  };
  const [form, setForm] = useState(emptyForm);
  const [showPasteArea, setShowPasteArea] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => { fetchData(); }, [search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const r = await api.get('/fizicka-lica', { params });
      setEntries(r.data);
    } catch { toast.error('Greska pri ucitavanju'); }
    finally { setLoading(false); }
  };

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  const totalPages = Math.ceil(entries.length / PAGE_SIZE);
  const paged = entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSubmit = async () => {
    if (!form.ime.trim() || !form.prezime.trim()) { toast.error('Unesite ime i prezime'); return; }
    // JMB validation: required for rezident (domace); optional for nerezident (strano)
    if ((form.vrsta_lica || 'domace') === 'domace') {
      if (!form.jmb || !form.jmb.trim()) { toast.error('JMB je obavezan za rezidenta'); return; }
      if (!/^\d{13}$/.test(form.jmb.trim())) { toast.error('JMB mora imati tačno 13 cifara'); return; }
    } else if (form.jmb && form.jmb.trim() && !/^\d{13}$/.test(form.jmb.trim())) {
      toast.error('JMB mora imati tačno 13 cifara'); return;
    }
    try {
      if (editingId) {
        await api.put(`/fizicka-lica/${editingId}`, form);
        toast.success('Sacuvano');
      } else {
        await api.post('/fizicka-lica', form);
        toast.success('Kreirano');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska'); }
  };

  const handleEdit = (e) => {
    setForm({
      ime: e.ime || '', prezime: e.prezime || '', jmb: e.jmb || '',
      djevojacko_prezime: e.djevojacko_prezime || '', ime_roditelja: e.ime_roditelja || '',
      datum_rodjenja: e.datum_rodjenja || '', mjesto_rodjenja: e.mjesto_rodjenja || '',
      opstina_rodjenja: e.opstina_rodjenja || '', drzava_rodjenja: e.drzava_rodjenja || '',
      pol: e.pol || '', drzavljanstvo: e.drzavljanstvo || '',
      vrsta_dokumenta: e.vrsta_dokumenta || '', broj_dokumenta: e.broj_dokumenta || '',
      dokument_izdat: e.dokument_izdat || '', stepen_strucnosti: e.stepen_strucnosti || '',
      zanimanje: e.zanimanje || '', osnov_prestanka: e.osnov_prestanka || '',
      drzava: e.drzava || '', opstina: e.opstina || '', mjesto: e.mjesto || '',
      adresa: e.adresa || '', telefon: e.telefon || '', email: e.email || '',
      client_id: e.client_id || '', napomena: e.napomena || '', aktivan: e.aktivan !== false,
      vrsta_lica: e.vrsta_lica || 'domace',
    });
    setEditingId(e.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati fizicko lice?'))) return;
    try { await api.delete(`/fizicka-lica/${id}`); toast.success('Obrisano'); fetchData(); }
    catch { toast.error('Greska'); }
  };

  // AI Parse (Gemini 3 Flash) - robust parser for any pasted text
  const handleAIParse = async () => {
    if (!pasteText || pasteText.length < 5) { toast.error('Zalijepite tekst iz web stranice'); return; }
    setAiParsing(true);
    try {
      const r = await api.post('/fizicka-lica/ai-parse', { text: pasteText });
      const parsed = r.data?.parsed || {};
      const count = r.data?.count || 0;
      if (!count) { toast.error('AI nije prepoznao nijedan podatak'); return; }
      setForm(prev => ({ ...prev, ...parsed }));
      setShowPasteArea(false);
      setPasteText('');
      toast.success(`AI uvezeno: ${parsed.prezime || ''} ${parsed.ime || ''} (${count} polja)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI greška - probajte ručno parsiranje');
    } finally {
      setAiParsing(false);
    }
  };

  // Parse pasted text from e.servis.mup.gov.me web page (regex fallback)
  const handleParsePaste = () => {
    if (!pasteText || pasteText.length < 5) { toast.error('Zalijepite tekst iz web stranice'); return; }
    const text = pasteText;
    // Remove zero-width spaces and clean lines
    const lines = text.replace(/[\u200B\u200C\u200D\uFEFF]/g, '').split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const parsed = {};

    // Exact line match - label is on its own line, value is on the next non-empty line
    const findVal = (...labels) => {
      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i];
        for (const label of labels) {
          if (line === label) {
            // Next non-empty line is the value
            for (let j = i + 1; j < Math.min(i + 3, lines.length); j++) {
              const val = lines[j];
              // Skip if val is another known label
              if (val && val.length > 0 && val.length < 100) return val;
            }
          }
        }
      }
      return '';
    };

    // Extract JMB (13 digits)
    const jmbMatch = text.match(/\b(\d{13})\b/);
    if (jmbMatch) parsed.jmb = jmbMatch[1];

    // Exact label matching from e.servis.mup.gov.me
    parsed.ime = findVal('Ime');
    parsed.prezime = findVal('Prezime');
    parsed.ime_roditelja = findVal('Ime oca');
    parsed.datum_rodjenja = findVal('Datum rođenja');
    parsed.mjesto_rodjenja = findVal('Mjesto rođenja');
    parsed.opstina_rodjenja = findVal('Grad rođenja');
    parsed.drzava_rodjenja = findVal('Država rođenja');
    parsed.drzavljanstvo = findVal('Državljanstvo');
    parsed.mjesto = findVal('Mjesto prebivališta');
    parsed.adresa = findVal('Adresa');
    parsed.broj_dokumenta = findVal('Id broj');

    const kucniBroj = findVal('Kućni broj');
    if (kucniBroj && parsed.adresa) {
      parsed.adresa = `${parsed.adresa} ${kucniBroj}`.trim();
    } else if (kucniBroj) {
      parsed.adresa = kucniBroj;
    }

    // Parse date: "18. avgust 1958." -> "1958-08-18"
    if (parsed.datum_rodjenja) {
      const months = { 'januar':'01','februar':'02','mart':'03','april':'04','maj':'05','jun':'06',
        'jul':'07','avgust':'08','septembar':'09','oktobar':'10','novembar':'11','decembar':'12' };
      const dm = parsed.datum_rodjenja.match(/(\d{1,2})\.\s*(\w+)\s*(\d{4})/);
      if (dm && months[dm[2].toLowerCase()]) parsed.datum_rodjenja = `${dm[3]}-${months[dm[2].toLowerCase()]}-${dm[1].padStart(2, '0')}`;
    }
    if (parsed.jmb && !parsed.datum_rodjenja) {
      const j = parsed.jmb;
      const yyy = parseInt(j.substring(4,7));
      const yr = yyy >= 800 ? (1000 + yyy) : (2000 + yyy);
      parsed.datum_rodjenja = `${yr}-${j.substring(2,4)}-${j.substring(0,2)}`;
    }
    // Always override from JMB if valid (deterministic DD MM YYY parsing)
    if (parsed.jmb && /^\d{13}$/.test(parsed.jmb)) {
      const j = parsed.jmb;
      const dd = parseInt(j.substring(0,2));
      const mm = parseInt(j.substring(2,4));
      const yyy = parseInt(j.substring(4,7));
      if (dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12) {
        const yr = yyy >= 800 ? (1000 + yyy) : (2000 + yyy);
        parsed.datum_rodjenja = `${yr}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
      }
    }
    if (parsed.jmb) {
      const gc = parseInt(parsed.jmb.substring(9,12));
      parsed.pol = gc < 500 ? 'M' : 'Z';
    }

    const updates = Object.fromEntries(Object.entries(parsed).filter(([_, v]) => v));
    if (Object.keys(updates).length === 0) { toast.error('Nije prepoznat nijedan podatak'); return; }
    setForm(prev => ({ ...prev, ...updates }));
    setShowPasteArea(false);
    setPasteText('');
    toast.success(`Uvezeno: ${parsed.prezime || ''} ${parsed.ime || ''} (JMB: ${parsed.jmb || '-'})`);
  };

  return (
    <div className="space-y-6" data-testid="fizicka-lica-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Fizicka lica</h3>
          <p className="text-sm text-slate-600 mt-1">Registar fizickih lica ({entries.length} zapisa)</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fl-add-btn">
          <Plus size={16} className="mr-2" /> Novo lice
        </Button>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchInput} onChange={e => handleSearchChange(e.target.value)}
            placeholder="Pretrazi po imenu, prezimenu, JMB, telefonu, emailu..."
            className="pl-9 bg-white border-slate-300 text-sm" data-testid="fl-search" />
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="p-12 text-center text-slate-500">Ucitavanje...</div>
      ) : paged.length === 0 ? (
        <Card className="p-12 text-center">
          <UserCircle size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">{entries.length === 0 ? 'Nema fizickih lica' : 'Nema rezultata'}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="fl-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Prezime</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Ime</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Vrsta</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">JMB</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum rodj.</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Mjesto</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Kvalifikacija</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Dokument</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(e => (
                  <tr key={e.id} className="border-b hover:bg-slate-50" data-testid={`fl-row-${e.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{e.prezime}</td>
                    <td className="px-4 py-3">{e.ime}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${e.vrsta_lica === 'strano' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                        {e.vrsta_lica === 'strano' ? 'Nerezident' : 'Rezident'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{e.jmb}</td>
                    <td className="px-4 py-3 text-xs">{fmtDate(e.datum_rodjenja)}</td>
                    <td className="px-4 py-3 text-xs">{e.mjesto || e.opstina || ''}</td>
                    <td className="px-4 py-3 text-xs">{e.zanimanje || '-'}</td>
                    <td className="px-4 py-3 text-xs">{e.broj_dokumenta || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setShowDetail(e)} className="p-2 hover:bg-blue-100 rounded" title="Detalji"><Eye size={16} className="text-blue-600" /></button>
                        <button onClick={() => handleEdit(e)} className="p-2 hover:bg-amber-100 rounded" title="Izmijeni"><Pencil size={16} className="text-amber-600" /></button>
                        <button onClick={() => handleDelete(e.id)} className="p-2 hover:bg-red-100 rounded" title="Obrisi"><Trash size={16} className="text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t flex items-center justify-between">
            <span className="text-xs text-slate-500">Str. {page}/{totalPages || 1} ({entries.length} ukupno)</span>
            {totalPages > 1 && (
              <div className="flex gap-1 items-center">
                <button onClick={() => { setPage(1); }} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">«</button>
                <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40"><CaretLeft size={12} /></button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return <button key={p} onClick={() => setPage(p)} className={`px-2.5 py-1 text-xs rounded border ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 hover:bg-slate-100'}`}>{p}</button>;
                })}
                <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40"><CaretRight size={12} /></button>
                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="px-2 py-1 text-xs rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">»</button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="fl-detail-dialog">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{showDetail.prezime} {showDetail.ime}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                {[
                  ['JMB', showDetail.jmb],
                  ['Djevojacko prezime', showDetail.djevojacko_prezime],
                  ['Ime roditelja', showDetail.ime_roditelja],
                  ['Datum rodjenja', fmtDate(showDetail.datum_rodjenja)],
                  ['Mjesto rodjenja', showDetail.mjesto_rodjenja],
                  ['Opstina rodjenja', showDetail.opstina_rodjenja],
                  ['Drzava rodjenja', showDetail.drzava_rodjenja],
                  ['Pol', showDetail.pol === 'M' ? 'Muski' : showDetail.pol === 'Z' ? 'Zenski' : '-'],
                  ['Drzavljanstvo', showDetail.drzavljanstvo],
                  ['Vrsta dokumenta', showDetail.vrsta_dokumenta],
                  ['Broj dokumenta', showDetail.broj_dokumenta],
                  ['Dokument izdat', showDetail.dokument_izdat],
                  ['Nivo obrazovanja', showDetail.stepen_strucnosti],
                  ['Stručna kvalifikacija', showDetail.zanimanje],
                  ['Osnov prestanka', showDetail.osnov_prestanka],
                  ['Drzava', showDetail.drzava],
                  ['Opstina', showDetail.opstina],
                  ['Mjesto', showDetail.mjesto],
                  ['Adresa', showDetail.adresa],
                  ['Telefon', showDetail.telefon],
                  ['Email', showDetail.email],
                ].map(([label, val]) => (
                  <div key={label} className="p-2 bg-slate-50 rounded border">
                    <span className="text-xs text-slate-500">{label}</span>
                    <p className="font-medium text-slate-800">{val || '-'}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="fl-form-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Izmijeni' : 'Novo fizicko lice'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {!editingId && (
              <div className="space-y-2">
                <Button onClick={() => setShowPasteArea(!showPasteArea)} variant="outline" size="sm" className="text-xs border-green-300 text-green-700 hover:bg-green-50 w-full" data-testid="fl-paste-import">
                  <ClipboardText size={14} className="mr-1" /> {showPasteArea ? 'Zatvori' : 'Uvezi iz web stranice (e.servis.mup.gov.me)'}
                </Button>
                {showPasteArea && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                    <p className="text-xs text-green-700">Kopirajte tekst sa stranice e.servis.mup.gov.me (Ctrl+A, Ctrl+C) i zalijepite ovdje:</p>
                    <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
                      placeholder="Zalijepite tekst ovdje..."
                      className="w-full rounded-md border border-green-300 bg-white px-3 py-2 text-xs min-h-[80px] resize-y" data-testid="fl-paste-area" />
                    <div className="grid grid-cols-2 gap-2">
                      <Button onClick={handleAIParse} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" disabled={!pasteText || aiParsing} data-testid="fl-ai-parse-btn">
                        {aiParsing ? 'AI analiza...' : '🤖 AI Prepoznaj (Gemini)'}
                      </Button>
                      <Button onClick={handleParsePaste} size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-100 text-xs" disabled={!pasteText || aiParsing} data-testid="fl-regex-parse-btn">
                        Ručno prepoznaj
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Prezime *</Label>
                <Input value={form.prezime} onChange={e => setForm({...form, prezime: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Ime *</Label>
                <Input value={form.ime} onChange={e => setForm({...form, ime: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>

            {/* Vrsta lica - Rezident / Nerezident */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 font-semibold">Vrsta lica *</Label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer text-sm ${form.vrsta_lica === 'domace' ? 'border-green-500 bg-green-50 text-green-800' : 'border-slate-200 text-slate-600'}`}>
                  <input type="radio" checked={form.vrsta_lica === 'domace'} onChange={() => setForm({...form, vrsta_lica: 'domace'})} data-testid="fl-vrsta-domace" />
                  <span><strong>Rezident (domaće)</strong></span>
                </label>
                <label className={`flex-1 flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer text-sm ${form.vrsta_lica === 'strano' ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-600'}`}>
                  <input type="radio" checked={form.vrsta_lica === 'strano'} onChange={() => setForm({...form, vrsta_lica: 'strano'})} data-testid="fl-vrsta-strano" />
                  <span><strong>Nerezident (strano)</strong></span>
                </label>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">JMB {(form.vrsta_lica || 'domace') === 'domace' && <span className="text-red-500">*</span>}</Label>
                <Input
                  value={form.jmb}
                  onChange={e => setForm({...form, jmb: e.target.value.replace(/\D/g, '').slice(0, 13)})}
                  maxLength={13}
                  placeholder="13 cifara"
                  className={`bg-white text-sm font-mono ${form.jmb && form.jmb.length === 13 ? 'border-green-500' : form.jmb && form.jmb.length > 0 ? 'border-amber-400' : 'border-slate-300'}`}
                  data-testid="fl-jmb-input"
                />
                {form.jmb && form.jmb.length > 0 && form.jmb.length !== 13 && (
                  <p className="text-[10px] text-amber-600">{form.jmb.length}/13 cifara</p>
                )}
              </div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Djevojacko prezime</Label>
                <Input value={form.djevojacko_prezime} onChange={e => setForm({...form, djevojacko_prezime: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Ime roditelja</Label>
                <Input value={form.ime_roditelja} onChange={e => setForm({...form, ime_roditelja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Datum rodjenja</Label>
                <DateInput value={form.datum_rodjenja} onChange={e => setForm({...form, datum_rodjenja: e.target.value})} className="border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Pol</Label>
                <select value={form.pol} onChange={e => setForm({...form, pol: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="">-</option><option value="M">Muski</option><option value="Z">Zenski</option>
                </select></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Drzavljanstvo</Label>
                <Input value={form.drzavljanstvo} onChange={e => setForm({...form, drzavljanstvo: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Mjesto rodjenja</Label>
                <Input value={form.mjesto_rodjenja} onChange={e => setForm({...form, mjesto_rodjenja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Opstina rodjenja</Label>
                <Input value={form.opstina_rodjenja} onChange={e => setForm({...form, opstina_rodjenja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Drzava rodjenja</Label>
                <Input value={form.drzava_rodjenja} onChange={e => setForm({...form, drzava_rodjenja: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Vrsta dokumenta</Label>
                <Input value={form.vrsta_dokumenta} onChange={e => setForm({...form, vrsta_dokumenta: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Broj dokumenta</Label>
                <Input value={form.broj_dokumenta} onChange={e => setForm({...form, broj_dokumenta: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Izdat od</Label>
                <Input value={form.dokument_izdat} onChange={e => setForm({...form, dokument_izdat: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Nivo obrazovanja</Label>
                <select value={form.stepen_strucnosti} onChange={e => setForm({...form, stepen_strucnosti: e.target.value})}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="">Izaberi...</option>
                  {NIVO_OBRAZOVANJA.map(n => <option key={n} value={n}>{n}</option>)}
                </select></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Stručna kvalifikacija</Label>
                <Input value={form.zanimanje} onChange={e => setForm({...form, zanimanje: e.target.value})} placeholder="npr: Ekonomista, Vozač..." className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Drzava</Label>
                <Input value={form.drzava} onChange={e => setForm({...form, drzava: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Opstina</Label>
                <Input value={form.opstina} onChange={e => setForm({...form, opstina: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Mjesto</Label>
                <Input value={form.mjesto} onChange={e => setForm({...form, mjesto: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500">Adresa</Label>
              <Input value={form.adresa} onChange={e => setForm({...form, adresa: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Telefon</Label>
                <Input value={form.telefon} onChange={e => setForm({...form, telefon: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Email</Label>
                <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>
            <div className="flex gap-3 justify-end pt-3 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fl-save-btn">
                {editingId ? 'Sacuvaj' : 'Kreiraj'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
